import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Mail, ArrowLeft, Check, ChevronLeft, ChevronRight, Calculator, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { trackVehicleView, trackClickToCall } from "@/utils/tracking";
import { generateVehicleStructuredData, generateBreadcrumbSchema } from "@/utils/vehicleStructuredData";
import { useSSRData } from "@/contexts/SSRDataContext";
import VehicleFeatures from "@/components/VehicleFeatures";
import PaymentCalculatorModal from "@/components/PaymentCalculatorModal";
import { OptimizedImage, getSupabaseOptimizedUrl } from "@/components/ui/optimized-image";
import carfaxLogo from "@/assets/carfax-logo.png";
import VehicleInquiryForm from "@/components/VehicleInquiryForm";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

const VehicleDetail = () => {
  const { id } = useParams();
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);

  // SSR data: server.js prefetches vehicle data and passes it through
  // React Context (SSRDataProvider). Available during both SSR and hydration.
  const ssrData = useSSRData();
  const ssrVehicle = ssrData.vehicle;
  const ssrImages = ssrData.vehicleImages;

  const { data: vehicle, isLoading } = useQuery({
    queryKey: ["vehicle", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("public_vehicles")
        .select("id, year, make, model, price, mileage, transmission, fuel_type, drivetrain, color, interior_color, vin, description, features, status, carfax_url, featured, created_at")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
    initialData: ssrVehicle?.id === id ? ssrVehicle : undefined,
  });

  const { data: images } = useQuery({
    queryKey: ["vehicle-images", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("public_vehicle_images")
        .select("*")
        .eq("vehicle_id", id)
        .order("is_primary", { ascending: false })
        .order("display_order");

      if (error) throw error;
      return data;
    },
    initialData: ssrImages && ssrImages.length > 0 && ssrImages[0]?.vehicle_id === id ? ssrImages : undefined,
  });

  const [api, setApi] = useState<CarouselApi>();

  // Auto slideshow
  // Manual slideshow removed in favor of Autoplay plugin



  // Track vehicle view when data is loaded
  useEffect(() => {
    if (vehicle) {
      trackVehicleView(vehicle);
    }
  }, [vehicle]);

  // Preload adjacent images for smoother carousel transitions


  const handleCallClick = () => {
    trackClickToCall('+16398990000');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 py-4">
          <div className="container mx-auto px-4">
            <p>Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 py-4">
          <div className="container mx-auto px-4">
            <p>Vehicle not found</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const primaryImage = images?.[0]?.image_url;
  const allImageUrls = images?.map(img => img.image_url).filter(Boolean);
  const structuredData = vehicle ? [
    generateVehicleStructuredData(vehicle, primaryImage, allImageUrls),
    generateBreadcrumbSchema(`${vehicle.year} ${vehicle.make} ${vehicle.model}`, vehicle.id),
  ] : undefined;

  return (
    <div className="min-h-screen flex flex-col">
      {vehicle && (
        <SEO
          title={`${vehicle.year} ${vehicle.make} ${vehicle.model} for Sale`}
          description={`Buy this ${vehicle.year} ${vehicle.make} ${vehicle.model} with ${vehicle.mileage.toLocaleString()} km. ${vehicle.transmission} transmission, ${vehicle.fuel_type} fuel. Price: $${Number(vehicle.price).toLocaleString()}. Located at Car Street, Langton ON.`}
          url={`https://carstreet.ca/vehicle/${vehicle.id}`}
          image={primaryImage}
          type="product"
          keywords={`${vehicle.year} ${vehicle.make} ${vehicle.model}, used ${vehicle.make} for sale, ${vehicle.make} ${vehicle.model} Ontario`}
          jsonLd={structuredData}
        />
      )}
      <Header />

      <main className="flex-1 py-2">
        <div className="container mx-auto px-4">
          {/* Back Button */}
          <Button variant="ghost" asChild className="mb-2">
            <Link to="/inventory">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Inventory
            </Link>
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4">
              {/* Image Gallery */}
              <div className="space-y-3">
                {images && images.length > 0 ? (
                  <>
                    <Carousel
                      opts={{
                        align: "center",
                        loop: true,
                        duration: 30,
                        dragFree: false,
                        skipSnaps: false,
                      }}
                      setApi={setApi}
                      className="w-full relative group"
                    >
                      <CarouselContent className="-ml-0">
                        {images.map((image, index) => (
                          <CarouselItem key={image.id} className="pl-0">
                            <div className="relative aspect-[4/3]">
                              <picture>
                                <OptimizedImage
                                  src={image.image_url}
                                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} - Image ${index + 1}`}
                                  width={1200}
                                  height={900}
                                  className="w-full h-full object-cover rounded-lg"
                                  loading={index === 0 ? "eager" : "lazy"}
                                  skipAnimation
                                  objectPosition={(image as any).object_position}
                                />
                              </picture>
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0" />
                      <CarouselNext className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0" />


                    </Carousel>

                    {images.length > 1 && (
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                        {images.map((image, index) => (
                          <div key={image.id} className="aspect-[4/3]">
                            <picture>
                              <OptimizedImage
                                src={image.image_url}
                                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} thumbnail ${index + 1}`}
                                width={200}
                                height={112}
                                className={`w-full h-full object-cover rounded cursor-pointer transition-all opacity-80 hover:opacity-100`}
                                onClick={() => api?.scrollTo(index)}
                                objectPosition={(image as any).object_position}
                              />
                            </picture>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-72 bg-muted rounded-lg flex items-center justify-center">
                    <p className="text-muted-foreground">No images available</p>
                  </div>
                )}
              </div>

              {/* Vehicle Title */}
              <div>
                <h1 className="text-3xl font-heading font-bold mb-3">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </h1>
              </div>

              {/* Carfax Report Button */}
              {vehicle.carfax_url && (
                <div className="flex justify-start mb-4">
                  <a href={vehicle.carfax_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity inline-block">
                    <img src={carfaxLogo} alt="Free CARFAX Report" className="h-8 object-contain" />
                  </a>
                </div>
              )}

              {/* Vehicle Features Section */}
              <VehicleFeatures
                mileage={vehicle.mileage}
                color={vehicle.color}
                transmission={vehicle.transmission}
                drivetrain={vehicle.drivetrain}
                fuelType={vehicle.fuel_type}
                interiorColor={vehicle.interior_color}
              />

              {/* Description */}
              {vehicle.description && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle>Description</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{vehicle.description}</p>
                  </CardContent>
                </Card>
              )}

              {/* Key Features */}
              {vehicle.features && vehicle.features.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle>Key Features</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {vehicle.features.map((feature, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>

            {/* Sidebar */}
            <div className="lg:self-start space-y-4">
              {/* Price Card */}
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center mb-4">
                    <p className="text-4xl font-heading font-bold text-price">
                      ${Number(vehicle.price).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">+ Tax & Licensing</p>
                  </div>

                  <div className="space-y-2">
                    <Button
                      variant="default"
                      className="w-full"
                      size="lg"
                      onClick={() => setIsCalculatorOpen(true)}
                    >
                      <Calculator className="mr-2 h-5 w-5" />
                      Estimate Your Payments
                    </Button>
                    <Button
                      variant="default"
                      className="w-full"
                      size="lg"
                      asChild
                    >
                      <Link to="/pre-approval">
                        <FileText className="mr-2 h-5 w-5" />
                        Get Pre-Approved
                      </Link>
                    </Button>
                    <Button
                      variant="default"
                      className="w-full"
                      size="lg"
                      onClick={() => {
                        handleCallClick();
                        window.location.href = 'tel:+16398990000';
                      }}
                    >
                      <Phone className="mr-2 h-5 w-5" />
                      Call Now
                    </Button>
                    <Button
                      variant="default"
                      className="w-full"
                      size="lg"
                      onClick={() => window.location.href = 'mailto:info@carstreet.ca'}
                    >
                      <Mail className="mr-2 h-5 w-5" />
                      Email Inquiry
                    </Button>

                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <h3 className="font-heading font-semibold mb-3 text-lg">Why Buy From Us?</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Price match guarantee</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Full vehicle inspection</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Warranty options available</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Flexible financing</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Vehicle Inquiry Form */}
              <VehicleInquiryForm
                vehicleId={vehicle.id}
                vehicleName={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              />
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Payment Calculator Modal */}
      <PaymentCalculatorModal
        isOpen={isCalculatorOpen}
        onClose={() => setIsCalculatorOpen(false)}
        vehiclePrice={Number(vehicle.price)}
      />
    </div>
  );
};

export default VehicleDetail;
